import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiChevronLeft, FiChevronRight, FiStar } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';

const TestimonialsSection = styled.section`
  padding: 6rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  background: rgba(26,26,46,0.92);
  border-radius: 24px;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: clamp(1.75rem, 6vw, 2.5rem);
  }
`;

const SectionSubtitle = styled(motion.p)<{ $isDarkMode: boolean }>`
  font-size: 1.2rem;
  color: ${props => props.$isDarkMode ? '#94a3b8' : '#64748b'};
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 3rem;
    padding: 0 1rem;
  }
`;

const TestimonialsContainer = styled.div`
  position: relative;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1));
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 20px;
  padding: 3rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '"';
    position: absolute;
    top: 1rem;
    left: 2rem;
    font-size: 4rem;
    color: rgba(139, 92, 246, 0.3);
    font-family: serif;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 16px;

    &::before {
      top: 0.5rem;
      left: 1rem;
      font-size: 3rem;
    }
  }
`;

const TestimonialText = styled.p`
  font-size: 1.2rem;
  color: #e2e8f0;
  line-height: 1.8;
  margin-bottom: 2rem;
  font-style: italic;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const AuthorName = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const AuthorRole = styled.p`
  font-size: 1rem;
  color: #8b5cf6;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const AuthorCompany = styled.p`
  font-size: 0.9rem;
  color: #94a3b8;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const Stars = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
  justify-content: center;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const Star = styled(FiStar)`
  color: #fbbf24;
  fill: #fbbf24;

  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
  }
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    gap: 0.75rem;
  }
`;

const NavButton = styled(motion.button)`
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  color: #8b5cf6;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #8b5cf6;
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    gap: 0.4rem;
  }
`;

const Dot = styled(motion.button)<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? '#8b5cf6' : 'rgba(139, 92, 246, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #8b5cf6;
    transform: scale(1.2);
  }

  @media (max-width: 768px) {
    width: 10px;
    height: 10px;
  }
`;

const Testimonials: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 1,
      text: "Briddy's technical expertise and collaborative spirit made our project a huge success. Her attention to detail and innovative solutions exceeded our expectations.",
      author: "Sarah olson",
      role: "Product Manager",
      company: "TechCorp Solutions",
      rating: 5
    },
    {
      id: 2,
      text: "Working with Bi was an absolute pleasure. She not only delivered exceptional code but also brought valuable insights that improved our entire development process.",
      author: "Michael Chen",
      role: "Lead Developer",
      company: "InnovateLab",
      rating: 5
    },
    {
      id: 3,
      text: "Briddy's automation solutions transformed our workflow. Her chatbot implementation reduced our response time by 80% and improved customer satisfaction significantly.",
      author: "Emily Rodriguez",
      role: "Operations Director",
      company: "CustomerFirst Inc",
      rating: 5
    },
    {
      id: 4,
      text: "Briddy is a true professional who combines technical excellence with excellent communication skills. She's always my first choice for complex projects.",
      author: "David Thompson",
      role: "CTO",
      company: "StartupXYZ",
      rating: 5
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <TestimonialsSection id="testimonials" ref={ref}>
      <SectionTitle
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={itemVariants}
      >
        What People Say
      </SectionTitle>
      
      <SectionSubtitle
        $isDarkMode={isDarkMode}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={itemVariants}
      >
        Testimonials from clients and collaborators
      </SectionSubtitle>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <TestimonialsContainer>
          <AnimatePresence mode="wait">
            <TestimonialCard
              key={currentIndex}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
            >
              <Stars>
                {Array.from({ length: testimonials[currentIndex].rating }).map((_, i) => (
                  <Star key={i} size={20} />
                ))}
              </Stars>
              <TestimonialText>
                {testimonials[currentIndex].text}
              </TestimonialText>
              <TestimonialAuthor>
                <AuthorName>{testimonials[currentIndex].author}</AuthorName>
                <AuthorRole>{testimonials[currentIndex].role}</AuthorRole>
                <AuthorCompany>{testimonials[currentIndex].company}</AuthorCompany>
              </TestimonialAuthor>
            </TestimonialCard>
          </AnimatePresence>

          <NavigationButtons>
            <NavButton
              onClick={prevTestimonial}
              disabled={currentIndex === 0}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiChevronLeft size={20} />
            </NavButton>
            <NavButton
              onClick={nextTestimonial}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiChevronRight size={20} />
            </NavButton>
          </NavigationButtons>

          <DotsContainer>
            {testimonials.map((_, index) => (
              <Dot
                key={index}
                active={index === currentIndex}
                onClick={() => goToTestimonial(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </DotsContainer>
        </TestimonialsContainer>
      </motion.div>
    </TestimonialsSection>
  );
};

export default Testimonials; 